import React from "react";
import BillPrintPages, { BillPrintLine } from "./BillPrintPages";
import ClinicPrintHeader, { ClinicPrintFooter, ClinicLogo } from "./ClinicPrintHeader";
import { BillingVisit, Contact, PrescriptionItem } from "../pages/billing/billingTypes";
import { medicineCategory, groupPrescriptionMedicines } from "../utils/medicineCategory";
import { formatFollowUpDate } from "../utils/followUpDate";
import { formatPatientName } from "../utils/patientName";
import { prescriptionDurationLabel, formatDoseSchedule as frequencySchedule } from "../utils/medicineDose";
import { formatBp, formatHeightParts, formatPulse, formatSpo2, formatTempF, formatWeight } from "../utils/vitalsFormat";

interface BillingPrintDocumentProps {
  visit: BillingVisit;
  billId?: number;
  contact?: Contact;
  date: string;
  billedBy?: string;
  includeBill?: boolean;
  patientAge?: string;
  lines?: BillPrintLine[];
  totalAmount?: number;
}

const calculateAge = (dob?: string) => {
  if (!dob) return "";
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
  return String(age);
};

const medicineBrandName = (item: PrescriptionItem) => {
  const medicine = item.medicine;
  return {
    brand: medicine?.brand || item.medicine_name || medicine?.name || "Medicine",
    name: medicine?.name && medicine.brand && medicine.name !== medicine.brand ? medicine.name : "",
  };
};

const chunk = <T,>(items: T[], size: number) => {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += size) pages.push(items.slice(i, i + size));
  return pages.length ? pages : [[]];
};

export const instructionText = (item: PrescriptionItem) => {
  const instructionDetails = (() => {
    if (Array.isArray(item.instruction_details)) return item.instruction_details;
    if (typeof item.instruction_details !== "string" || !item.instruction_details.trim()) return [];
    try {
      const parsed = JSON.parse(item.instruction_details);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();
  const detailText = instructionDetails.map((detail) => detail?.label).filter(Boolean).join(", ");
  return [detailText, item.instructions].filter(Boolean).join(", ") || "-";
};

const BillingPrintDocument: React.FC<BillingPrintDocumentProps> = ({ visit, billId, contact, date, billedBy = "-", lines = [], totalAmount = 0, includeBill = true, patientAge }) => {
  const vital = visit.vitals?.[0];
  const prescriptionItemsPerPage = 7;
  const prescriptionPages = chunk(groupPrescriptionMedicines(visit.prescription?.items || []), prescriptionItemsPerPage);
  return (
              <div className="mx-auto flex w-full max-w-[900px] [zoom:0.8] flex-col gap-6 print:block print:w-full print:max-w-none print:[zoom:1] print:gap-0 print:overflow-visible">
                {prescriptionPages.map((pageItems, pageIndex) => (
                  <section key={`rx-page-${pageIndex}`} className={`flex min-h-[1120px] flex-col bg-white p-6 shadow print:box-border print:h-[287mm] print:min-h-0 print:w-[210mm] print:p-[10mm] print:shadow-none print:overflow-hidden ${includeBill || pageIndex < prescriptionPages.length - 1 ? "print:break-after-page" : "print:break-after-auto"}`}>
                    <ClinicPrintHeader doctor={visit.assigned_doctor} />

                    <div className="grid grid-cols-3 gap-3 border-b-2 border-[#2d2a78] py-3 text-sm font-bold text-[#2d2a78]">
                      <div>Name: <span className="font-semibold text-text-light">{formatPatientName(contact)}</span></div>
                      <div>Age/Gender: <span className="font-semibold text-text-light">{patientAge ?? (calculateAge(contact?.dob) || "-")} / {contact?.gender || "-"}</span></div>
                      <div>Date: <span className="font-semibold text-text-light">{date}</span></div>
                    </div>

                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 whitespace-nowrap border-b-2 border-[#2d2a78] py-3 text-sm font-bold text-[#2d2a78]">
                      <div>BP: <span className="font-semibold text-text-light">{formatBp(vital?.bp_systolic, vital?.bp_diastolic)}</span></div>
                      <div>PR: <span className="font-semibold text-text-light">{formatPulse(vital?.pulse_rate)}</span></div>
                      <div>SpO2: <span className="font-semibold text-text-light">{formatSpo2(vital?.oxygen_saturation)}</span></div>
                      <div>Temp: <span className="font-semibold text-text-light">{formatTempF(vital?.temperature)}</span></div>
                      {(vital?.height || vital?.height_feet || vital?.height_inches) && (
                        <div>Height: <span className="font-semibold text-text-light">{formatHeightParts(vital?.height_feet, vital?.height_inches, vital?.height)}</span></div>
                      )}
                      <div>Weight: <span className="font-semibold text-text-light">{formatWeight(vital?.weight)}</span></div>
                    </div>

                    <div className="grid min-h-[760px] flex-1 grid-cols-[32%_68%] border-b-2 border-[#2d2a78] text-[#2d2a78] print:min-h-0">
                      <div className="space-y-16 border-r-2 border-[#2d2a78] p-4 text-sm font-bold">
                        {pageIndex === 0 ? (
                          <>
                            <div>
                              <div>Chief complaints:</div>
                              <p className="mt-2 font-semibold text-text-light">{visit.clinical_record?.chief_complaint || "-"}</p>
                            </div>
                            <div>
                              <div>Diagnosis:</div>
                              <p className="mt-2 font-semibold text-text-light">{visit.clinical_record?.diagnosis || "-"}</p>
                            </div>
                            <div>
                              <div>O/E:</div>
                              <p className="mt-2 font-semibold text-text-light">{visit.clinical_record?.notes || "-"}</p>
                            </div>
                            <div>
                              <div>Advice:</div>
                              <p className="mt-2 font-semibold text-text-light">{visit.clinical_record?.follow_up_notes || "-"}</p>
                            </div>
                            {visit.clinical_record?.follow_up_date && <div>
                              <div>Follow-up Date:</div>
                              <p className="mt-2 font-semibold text-text-light">{formatFollowUpDate(visit.clinical_record.follow_up_date)}</p>
                            </div>}
                          </>
                        ) : (
                          <div>Prescription continued</div>
                        )}
                      </div>

                      <div className="relative p-4">
                        <div className="pointer-events-none absolute left-1/2 top-[45%] hidden -translate-x-1/2 -translate-y-1/2 opacity-25 print:block">
                          <ClinicLogo className="h-72 w-60" watermark />
                        </div>
                        <div className="mb-2 text-2xl font-black text-[#2d2a78]">Rx</div>
                        <table className="w-full table-fixed border-collapse text-xs leading-tight text-text-light">
                          <colgroup>
                            <col className="w-[6%]" />
                        <col className="w-[35%]" />
                        <col className="w-[15%]" />
                        <col className="w-[12%]" />
                        <col className="w-[8%]" />
                        <col className="w-[24%]" />
                          </colgroup>
                          <thead className="bg-[#2d2a78] text-white">
                            <tr>
                              <th className="border px-1.5 py-1 text-left">S.No</th>
                              <th className="border px-1.5 py-1 text-left">Medicine</th>
                              <th className="border px-1.5 py-1 text-left">Dose / Frequency</th>
                              <th className="border px-1.5 py-1 text-left">Duration</th>
                          <th className="border px-1.5 py-1 text-center">Qty</th>
                              <th className="border px-1.5 py-1 text-left">Instruction</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageItems.length ? pageItems.map((item, index) => {
                              const medicineName = medicineBrandName(item);
                              return (
                                <React.Fragment key={`rx-medicine-${item.id || item.medicine_id || index}`}>
                                  {(index === 0 || medicineCategory(pageItems[index - 1].medicine) !== medicineCategory(item.medicine)) && (
                                    <tr className="bg-primary/10 text-[#2d2a78]">
                                      <th colSpan={6} scope="colgroup" className="border px-1.5 py-1 text-left font-bold">{medicineCategory(item.medicine)}</th>
                                    </tr>
                                  )}
                                  <tr>
                                  <td className="border px-1.5 py-1 align-top">{pageIndex * prescriptionItemsPerPage + index + 1}</td>
                                  <td className="border px-1.5 py-1 align-top">
                                    <div className="font-bold">{medicineName.brand}</div>
                                    {medicineName.name && <div className="text-xs text-subtext-light">{medicineName.name}</div>}
                                  </td>
                                  <td className="border px-1.5 py-1 align-top">{frequencySchedule(item)}</td>
                                  <td className="border px-1.5 py-1 align-top">{prescriptionDurationLabel(item.duration, item.frequency)}</td>
                            <td className="border px-1.5 py-1 text-center align-top">{item.quantity ?? "-"}</td>
                                  <td className="border px-1.5 py-1 align-top">{instructionText(item)}</td>
                                </tr>
                                </React.Fragment>
                              );
                            }) : (
                              <tr>
                                <td colSpan={6} className="border p-3 text-center text-subtext-light">No prescription medicines found.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="mt-6 print:mt-3">
                      <ClinicPrintFooter />
                    </div>
                  </section>
                ))}

                {includeBill && <BillPrintPages
                  billId={billId}
                  contact={contact}
                  date={date}
                  billedBy={billedBy}
                  lines={lines}
                  totalAmount={totalAmount}
                />}
              </div>
  );
};

export default BillingPrintDocument;
